import { DateClickArg } from '@fullcalendar/interaction';
import axios from 'axios';
import DayCell from 'components/MonthView/DayCell/DayCell';
import MonthView from 'components/MonthView/MonthView';
import { ITimeSlot } from 'interfaces/timeslotType';
import { chunk } from 'lodash';
import moment from 'moment';
import { mergePractitionersAvaiBlocks } from 'pages/UpdatedSearchResultPage/utils/mergePractitionersAvaiBlocks';
import { FC, useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getClinicAvailability } from 'services/APIs/getClinicAvailability';
import { getPractitionerAvailability } from 'services/APIs/getPractitionerAvailability';
import {
  convertToDate,
  getAllDatesWithinYearFromDate,
  getDateRange,
} from 'utils/dates';
import { isDateInThePast } from 'utils/isDateInThePast';
import { parseQueryUrl } from 'utils/parseQueryUrl';
import { getSlotsPer30MinutesForAvailability } from 'utils/updatedSlotsPer30Minutes';
import { useAppSelector } from 'redux/reduxHooks';

interface MonthViewBookNowPanelProps {
  clinicId: string;
  serviceId: string;
  practitionerId: string | null;
  date: string;
  clinicTimezone: string;
  onNavigateToDayView: (dateStr: string) => void;
  slotInterval: number;
  isLoading: boolean;
  setIsLoading: (isLoaded: boolean) => void;
  dentistId: string;
}

const MonthViewBookNowPanel: FC<MonthViewBookNowPanelProps> = ({
  clinicId,
  clinicTimezone,
  date,
  onNavigateToDayView,
  practitionerId,
  serviceId,
  slotInterval,
  isLoading,
  setIsLoading,
  dentistId,
}) => {
  const location = useLocation();
  const [timeSlots, setTimeSlots] = useState<ITimeSlot[]>([]);
  const [closedDates, setClosedDates] = useState<string[]>([]);
  const [isLoadedDates, setIsLoadedDates] = useState<string[]>([]);

  const { codeUrl, serviceSlugNameUrl } = parseQueryUrl(location);
  const selectedLastInsurance = useAppSelector(
    (state) => state.bookingFormSlice.selectedLastInsurance
  );
  const lastInsuranceCarrierId = selectedLastInsurance?.insuranceCarrierId;

  const axiosCancelTokenRef = useRef<any>(null);

  const currentDate = moment().format('YYYY-MM-DD');

  const allDatesWithinAYear = getAllDatesWithinYearFromDate(currentDate);

  const fetchAvailabilityInDateRangeForClinic = useCallback(
    async (
      dates: string[],
      source: any
    ): Promise<{
      closedDates: string[];
      timeSlots: ITimeSlot[];
    }> => {
      const tmp = await getClinicAvailability({
        clinicId,
        serviceId,
        dates,
        timezone: clinicTimezone,
        timeBlocks: [0],
        ...(codeUrl && { code: codeUrl }),
        ...(serviceSlugNameUrl && {
          serviceSlugName: serviceSlugNameUrl,
        }),
        ...(lastInsuranceCarrierId && { insuranceId: lastInsuranceCarrierId }),
        dentistId,
        cancelToken: source.token,
      });

      const doctorsByClinic = tmp?.doctors || [];

      const availBlocks = mergePractitionersAvaiBlocks(
        doctorsByClinic,
        slotInterval
      );

      const closedDates = doctorsByClinic.reduce((accum: any, doctor: any) => {
        const closedDates = doctor.availableBlocks
          .filter((item: any) => item.isClosed)
          .map((item: any) => item.date);

        return accum.concat(closedDates);
      }, []);

      const timeSlots = Object.keys(availBlocks.dateItems).reduce(
        (accum: any, date: any) => {
          accum.push({
            date,
            blocks: Object.keys(availBlocks.dateItems[date]),
          });
          return accum;
        },
        []
      );

      return {
        closedDates,
        timeSlots,
      };
    },
    [
      clinicId,
      clinicTimezone,
      codeUrl,
      dentistId,
      serviceId,
      serviceSlugNameUrl,
      slotInterval,
      lastInsuranceCarrierId,
    ]
  );

  const fetchAvailabilityInDateRangeForPractitioner = useCallback(
    async (
      practitionerId: string,
      dates: string[],
      source: any
    ): Promise<{
      timeSlots: ITimeSlot[];
    }> => {
      const { availableBlocks: availBlocks } =
        await getPractitionerAvailability({
          clinicId,
          serviceId,
          practitionerId,
          dates,
          timezone: clinicTimezone,
          timeBlocks: [0],
          ...(codeUrl && { code: codeUrl }),
          ...(serviceSlugNameUrl && {
            serviceSlugName: serviceSlugNameUrl,
            cancelToken: source.token,
          }),
          dentistId,
        });

      const updatedSlotsPer30Minutes = getSlotsPer30MinutesForAvailability(
        availBlocks,
        slotInterval
      );

      return {
        timeSlots: updatedSlotsPer30Minutes,
      };
    },
    [
      clinicId,
      clinicTimezone,
      codeUrl,
      dentistId,
      serviceId,
      serviceSlugNameUrl,
      slotInterval,
    ]
  );

  const fetchWeeklyTimeSlot = useCallback(
    async (startDate: Date, endDate: Date) => {
      setIsLoading(false);

      const dateStrings = getDateRange(startDate, endDate);

      const dateWithinAYear = dateStrings.filter((date) => {
        return allDatesWithinAYear.includes(date);
      });

      const datesInFuture = dateWithinAYear.filter(
        (dateString) => !isDateInThePast(dateString)
      );

      if (axiosCancelTokenRef.current !== null) {
        axiosCancelTokenRef.current.cancel();
      }

      const CancelToken = axios.CancelToken;
      axiosCancelTokenRef.current = CancelToken.source();

      setIsLoadedDates([]);
      setTimeSlots([]);

      const tmpDatesInFuture = [...datesInFuture];

      const chunkDateInFuture = chunk(tmpDatesInFuture, 5);

      const chunkAmountCalls = chunk(chunkDateInFuture, 3);

      if (practitionerId) {
        // Call weekly API of practitioner
        for (const dateRanges of chunkAmountCalls) {
          await Promise.all(
            dateRanges.map(async (futureRange) => {
              if (futureRange.length === 1) return;

              const dates = [
                futureRange[0],
                futureRange[futureRange.length - 1],
              ];

              const { timeSlots } =
                await fetchAvailabilityInDateRangeForPractitioner(
                  practitionerId,
                  dates,
                  axiosCancelTokenRef.current
                );

              setIsLoadedDates((state: any) => {
                return [...state, ...futureRange];
              });

              setTimeSlots((currentTimeSlots) => [
                ...currentTimeSlots,
                ...timeSlots,
              ]);

              return;
            })
          );
        }

        return;
      }

      // Call weekly API of clinic
      for (const dateRanges of chunkAmountCalls) {
        await Promise.all(
          dateRanges.map(async (futureRange) => {
            if (futureRange.length === 1) return;

            const dates = [futureRange[0], futureRange[futureRange.length - 1]];

            const { closedDates, timeSlots } =
              await fetchAvailabilityInDateRangeForClinic(
                dates,
                axiosCancelTokenRef.current
              );

            setIsLoadedDates((state: any) => {
              return [...state, ...futureRange];
            });

            setTimeSlots((currentTimeSlots) => [
              ...currentTimeSlots,
              ...timeSlots,
            ]);

            setClosedDates((state: string[]) => {
              return [...state, ...closedDates];
            });

            return;
          })
        );
      }
    },
    [
      allDatesWithinAYear,
      fetchAvailabilityInDateRangeForClinic,
      fetchAvailabilityInDateRangeForPractitioner,
      practitionerId,
      setIsLoading,
    ]
  );

  const onDateCellClicked = (arg: DateClickArg) => {
    const today = moment();
    if (moment(arg.dateStr, 'YYYY-MM-DD').isBefore(today, 'd')) return;

    if (!isLoadedDates.includes(arg.dateStr)) return;

    onNavigateToDayView(arg.dateStr);
  };

  return (
    <MonthView
      isRenderPast
      contentHeight={452}
      selectedDate={date}
      dayCellContent={(props) => {
        const date = moment(convertToDate(props.date)).format('YYYY-MM-DD');
        const isWithinAYear = allDatesWithinAYear.includes(date);

        return (
          <DayCell
            isLoading={
              isWithinAYear &&
              (isLoading || (props.isFuture && !isLoadedDates.includes(date)))
            }
            {...props}
            timeslots={timeSlots}
            {...(!practitionerId && { closedDates })}
          />
        );
      }}
      practitionerId={practitionerId}
      serviceId={serviceId}
      dateClick={onDateCellClicked}
      fetchTimeSlots={fetchWeeklyTimeSlot}
      dentistId={dentistId}
    />
  );
};

export default MonthViewBookNowPanel;
